import React from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  FileText,
  Calendar,
  Briefcase,
  Download,
  MapPin,
} from 'lucide-react';

export const ViewApplicationModal = (props) => {
  const { isOpen, onClose, application } = props;

  if (!isOpen || !application) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'Under Review':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'Shortlisted':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'Accepted':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Rejected':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto'>
      <div className='bg-card rounded-lg shadow-xl w-full max-w-3xl border border-border my-8 max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border bg-card'>
          <h3 className='text-lg font-semibold text-foreground'>Application Details</h3>
          <button
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground transition-colors'
            type='button'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Status Section */}
          <div className='mb-6 pb-6 border-b border-border'>
            <div className='flex items-center gap-3 mb-4'>
              <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(application.status)}`}>
                {application.status}
              </span>
            </div>
            {application.jobId && (
              <div className='flex items-start gap-3'>
                <Briefcase size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                <div>
                  <p className='text-xs text-muted-foreground'>Applied For</p>
                  <p className='text-sm font-semibold text-foreground'>{application.jobId.jobTitle}</p>
                </div>
              </div>
            )}
          </div>

          {/* Applicant Information */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>
                Applicant Information
              </h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <User size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Name</p>
                    <p className='text-sm text-foreground'>{application.fullName || 'N/A'}</p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Mail size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Email</p>
                    <p className='text-sm text-foreground break-all'>{application.email || 'N/A'}</p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Phone size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Phone</p>
                    <p className='text-sm text-foreground'>{application.phoneNumber || 'N/A'}</p>
                  </div>
                </div>

                {application.address && (
                  <div className='flex items-start gap-3'>
                    <MapPin size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                    <div>
                      <p className='text-xs text-muted-foreground'>Address</p>
                      <p className='text-sm text-foreground'>{application.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Application Details */}
            <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>
                Application Details
              </h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <Calendar size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Applied On</p>
                    <p className='text-sm text-foreground'>{formatDate(application.createdAt)}</p>
                  </div>
                </div>

                {application.resumeUrl && (
                  <div className='flex items-start gap-3'>
                    <FileText size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                    <div>
                      <p className='text-xs text-muted-foreground'>Resume</p>
                      <a
                        href={application.resumeUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-2 text-sm text-primary hover:underline'
                      >
                        <Download size={14} />
                        Download Resume
                      </a>
                    </div>
                  </div>
                )}

                {application.coverLetter && (
                  <div className='flex items-start gap-3'>
                    <FileText size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                    <div>
                      <p className='text-xs text-muted-foreground'>Cover Letter</p>
                      <div className='mt-2 p-3 bg-muted/30 border border-border rounded-lg'>
                        <p className='text-sm text-foreground whitespace-pre-wrap'>{application.coverLetter}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Notes */}
            {application.notes && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                  <FileText size={16} />
                  Notes
                </h5>
                <div className='p-4 bg-muted/30 border border-border rounded-lg'>
                  <p className='text-sm text-foreground whitespace-pre-wrap'>{application.notes}</p>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className='md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border'>
              <div className='flex items-start gap-3'>
                <Calendar size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                <div>
                  <p className='text-xs text-muted-foreground'>Submitted At</p>
                  <p className='text-sm text-foreground'>{formatDate(application.createdAt)}</p>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <Calendar size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                <div>
                  <p className='text-xs text-muted-foreground'>Last Updated</p>
                  <p className='text-sm text-foreground'>{formatDate(application.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='sticky bottom-0 px-6 py-4 border-t border-border bg-card'>
          <button
            onClick={onClose}
            className='w-full px-4 py-2 bg-muted text-foreground rounded-md hover:bg-accent transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
